import { useLayoutEffect, useState, useMemo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DROPDOWN_KEYS, ROUTE_PATHS } from "../../common/enums/routing-enums";
import { returnRoutesByKey } from "../../common/util/Navigation/createMenuConfig";
import { createNavigationConfig } from "../../common/util/Navigation/createNavigationConfig";
import type { NavigationApiResponse } from "../../services/MyBizShell/mybizShellAPI.types";
import { useAppDispatch, useAppSelector } from "../../store";
import { setLocalStorageItem } from "../../reducers/loginLocalStorage";
import defaultContent from "../../content/navigation";
import defaultCommonContent from "../../content/common";

const PATHS_TO_IGNORE = ["/", ROUTE_PATHS.PERFORMANCE_DASHBOARD, ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD];

const ROUTING_GROUPS = [
  DROPDOWN_KEYS.PERFORMANCE,
  DROPDOWN_KEYS.EARLY_INCENTIVES,
  DROPDOWN_KEYS.MY_GROUP,
  DROPDOWN_KEYS.LEADER_GROWTH,
];

type TrackablePage = { path: string; labelKey: string; label: string };

interface NewNavSmartNextStepsProps {
  onClose: () => void;
  navigationData?: NavigationApiResponse;
}

export const NewNavSmartNextSteps = ({ onClose, navigationData }: NewNavSmartNextStepsProps) => {
  const { t } = useTranslation(["navigation", "common"]);
  const location = useLocation();
  const dispatch = useAppDispatch();

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  const { shellConfig, isBootLoading, configuration, user } = useAppSelector((state) => state.boot);
  const { pageViewCounts: storedPageViewCounts } = useAppSelector((state) => state.loginLocalStorage);
  const { menuConfig = [] } = shellConfig;
  const showWhosNewIncludesCustomers = configuration?.actionReports?.showWhosNewIncludesCustomers ?? false;

  // Memoize availablePages to prevent expensive recalculation on every render
  const availablePages = useMemo<TrackablePage[]>(() => {
    if (navigationData) {
      const navConfig = createNavigationConfig(navigationData, undefined, user.aff);
      const actionReports = showWhosNewIncludesCustomers
        ? navConfig.groupMgmtActionReports.map((item) =>
            item.id === "whosNew" ? { ...item, contentKey: "whosNewWithCustomers" } : item,
          )
        : navConfig.groupMgmtActionReports;
      return [...navConfig.performanceIncome, ...navConfig.groupMgmtNavItems, ...actionReports]
        .filter((item) => !PATHS_TO_IGNORE.includes(item.path))
        .map((item) => ({ path: item.path, labelKey: item.contentKey ?? item.id, label: item.label }));
    }
    return ROUTING_GROUPS.flatMap((group) => returnRoutesByKey(group, menuConfig))
      .filter((page) => !PATHS_TO_IGNORE.includes(page.path))
      .map((page) => ({
        path: page.path,
        labelKey: page.label,
        label: (defaultContent.linkTitle as Record<string, string>)[page.label] ?? page.label,
      }));
  }, [navigationData, menuConfig, showWhosNewIncludesCustomers, user.aff]);

  const getDefaultPageViews = (): Record<string, number> => {
    const defaults: Record<string, number> = {};
    (configuration?.defaultPageViews || []).forEach((page: string) => {
      const path = ROUTE_PATHS[page as keyof typeof ROUTE_PATHS];
      if (path && !PATHS_TO_IGNORE.includes(path)) {
        defaults[path] = 0;
      }
    });
    return defaults;
  };

  // Memoize getTopPages to avoid recreating on every render
  const getTopPages = useCallback(
    (counts: Record<string, number>): TrackablePage[] => {
      const defaults = getDefaultPageViews();
      const filtered: Record<string, number> = {};

      Object.entries(counts).forEach(([path, count]) => {
        if (availablePages.some((page) => page.path === path)) {
          filtered[path] = count;
        }
      });

      if (Object.keys(filtered).length < 3) {
        for (const path in defaults) {
          if (!filtered[path] && Object.keys(filtered).length < 3) {
            filtered[path] = 0;
          }
        }
      }

      // Sort by count descending, then take top 3
      return Object.entries(filtered)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .reduce<TrackablePage[]>((acc, [path]) => {
          const page = availablePages.find((availablePage) => availablePage.path === path);
          if (page) acc.push(page);
          return acc;
        }, []);
    },
    [availablePages, configuration],
  );

  const [pageViewCounts, setPageViewCounts] = useState<Record<string, number>>(
    () => storedPageViewCounts as Record<string, number>,
  );
  const [topPages, setTopPages] = useState<TrackablePage[]>(() =>
    getTopPages(storedPageViewCounts as Record<string, number>),
  );
  const [currentPath, setCurrentPath] = useState("");

  // Track page views without causing infinite loops
  useLayoutEffect(() => {
    const previousPath = sessionStorage.getItem("previousPath");
    if (location.pathname !== currentPath && location.pathname !== previousPath) {
      sessionStorage.setItem("previousPath", location.pathname);

      const trackedPage = availablePages.find((page) => page.path === location.pathname);
      if (trackedPage) {
        setCurrentPath(location.pathname);

        // Use functional state update to avoid depending on current pageViewCounts
        setPageViewCounts((currentCounts) => {
          const updated = {
            ...currentCounts,
            [location.pathname]: (currentCounts[location.pathname] ?? 0) + 1,
          };
          const sorted = Object.fromEntries(Object.entries(updated).sort(([, a], [, b]) => b - a));

          // Update top pages and persist to localStorage
          setTopPages(getTopPages(sorted));

          // Wrap localStorage update in try-catch to handle quota exceeded errors
          try {
            dispatch(setLocalStorageItem({ pageViewCounts: sorted }));
          } catch (error) {
            console.error("Failed to persist page view counts to localStorage:", error);
            // Continue gracefully - feature still works without persistence
          }

          return sorted;
        });
      }
    }
  }, [location.pathname, currentPath, availablePages, dispatch, getTopPages]);

  if (isBootLoading) {
    return <div className="loading-pulse" style={{ height: "7.5rem" }} />;
  }

  // Don't render anything if no pages are available
  if (topPages.length === 0) {
    return null;
  }

  return (
    <div className="MobileNavigationMenu__link-list">
      {topPages.map((page) => (
        <Link
          key={page.path}
          to={{ pathname: page.path }}
          className="MobileNavigationMenu__link-item"
          onClick={onClose}
          aria-label={t(`linkTitle.${page.labelKey}`, page.label, { symbol: disclaimerSymbol })}
        >
          <span className="MobileNavigationMenu__link-text">
            {t(`linkTitle.${page.labelKey}`, page.label, { symbol: disclaimerSymbol })}
          </span>
        </Link>
      ))}
    </div>
  );
};
